function isHiddenWithin(node: HTMLElement, container: HTMLElement): boolean {
  if (getComputedStyle(node).visibility === 'hidden') return true;
  let current: HTMLElement | null = node;
  while (current && current !== container) {
    if (getComputedStyle(current).display === 'none') return true;
    current = current.parentElement;
  }
  return false;
}

function isTabbableCandidate(node: Node): boolean {
  const el = node as HTMLElement & { disabled?: boolean };
  const isHiddenInput = el instanceof HTMLInputElement && el.type === 'hidden';
  if (el.disabled || el.hidden || isHiddenInput) return false;
  return el.tabIndex >= 0;
}

/** Tabbable elements inside `container` in DOM order, mirroring Radix FocusScope's candidate list, which it does not export. */
export function getTabbables(container: HTMLElement): HTMLElement[] {
  const nodes: HTMLElement[] = [];
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_ELEMENT, {
    acceptNode: (node) => (isTabbableCandidate(node) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP),
  });
  while (walker.nextNode()) nodes.push(walker.currentNode as HTMLElement);
  return nodes.filter((el) => !isHiddenWithin(el, container));
}

/**
 * First tabbable element after `after` in document order, or null when there is none.
 * Skips `exclude` and its subtree, `inert` regions and Radix focus guards, so a portalled popover
 * can hand Tab on to whatever follows its anchor in the page.
 */
export function getNextTabbable(after: HTMLElement, exclude?: HTMLElement | null): HTMLElement | null {
  const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_ELEMENT, {
    acceptNode: (node) => {
      const el = node as HTMLElement;
      if (el === exclude || el.hasAttribute('inert') || el.hasAttribute('data-radix-focus-guard')) {
        return NodeFilter.FILTER_REJECT;
      }
      return isTabbableCandidate(el) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP;
    },
  });
  walker.currentNode = after;
  let next = walker.nextNode() as HTMLElement | null;
  while (next && isHiddenWithin(next, document.body)) next = walker.nextNode() as HTMLElement | null;
  return next;
}
